import React, { memo, useRef } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { a } from "../appColors";
import ScrollLinkedBackdrop from "./ScrollLinkedBackdrop";

const DOT_GAP = 14;

/**
 * The ground a paper sits on (Figma 191:37).
 *
 * The same dotted surface as the classroom board, so the two halves of the
 * product read as one place. Painted with a gradient rather than nodes: it
 * covers the whole window and must cost nothing to scroll over.
 */
export const dottedSurface = (isDark) => ({
  backgroundImage: `radial-gradient(${
    isDark ? "rgba(255,255,255,0.09)" : "rgba(34,34,34,0.09)"
  } 1px, transparent 1px)`,
  backgroundSize: `${DOT_GAP}px ${DOT_GAP}px`,
});

/**
 * Header along the top, questions on the left, the paper in the middle, tools
 * on the right.
 *
 * Pure layout: it takes each region as a slot and owns none of them, so the
 * same frame serves solving, review and anything else that needs a paper
 * beside a list.
 */
const PaperCanvas = ({
  header,
  sidebar,
  children,
  toolbar,
  overlay,
  /** A layer under everything in the row, filling it edge to edge: the
   *  board for the open page around the paper. */
  backdrop,
  /** The paper's scrolling box. The backdrop and the dots follow it, so the
   *  page and the paper scroll as one sheet. */
  scrollRef,
  /** How wide the list beside the paper is, so the paper can be centred on
   *  the window rather than on whatever room the list left behind. */
  gutter = 0,
  isDark = false,
}) => {
  const rootRef = useRef(null);
  return (
    /**
     * Exactly one viewport tall, and it never scrolls.
     *
     * The page used to grow past the window, so the whole thing scrolled and
     * the toolbar and the question list travelled with it. Now the canvas is
     * fixed at the window's height and the two frames inside it scroll on
     * their own — the list when a paper has many questions, the paper when a
     * question has many parts. The page around the paper, and whatever is
     * drawn on it, scrolls with the paper as one sheet; the header, list and
     * tools stay where they were put.
     */
    <Flex
      ref={rootRef}
      direction="column"
      height="100vh"
      overflow="hidden"
      bg={a.page}
      sx={dottedSurface(isDark)}
    >
      {header}

      <Flex
        align="stretch"
        gap={{ base: "10px", md: "18px" }}
        px={{ base: "12px", md: "24px" }}
        pb={{ base: "12px", md: "20px" }}
        flex="1"
        minH={0}
        position="relative"
      >
        {/* The dots are somewhere to work too. This layer sits under the list
            and the paper and fills the rest of the row, so a note, a shape or
            a line can go anywhere on the page, not only on the paper. The
            list and the paper are raised above it, so they still take their
            own presses. */}
        {backdrop && (
          <ScrollLinkedBackdrop scrollRef={scrollRef} dotsRef={rootRef}>
            {backdrop}
          </ScrollLinkedBackdrop>
        )}

        <Flex position="relative" zIndex={1} flexShrink={0} minH={0} data-own-scroll>
          {sidebar}
        </Flex>

        {/* minW 0 lets the paper's own contents wrap instead of pushing the
            tools off the edge; minH 0 is what lets it scroll inside rather
            than stretching this row past the window. */}
        {/* The column lets presses through to the page on either side of the
            paper; the paper takes its own back (see PaperPanel). */}
        <Box flex="1" minW={0} minH={0} position="relative" zIndex={1} pointerEvents="none">
          {children}
          {overlay}
        </Box>

        {/* A mirror of the list. Without it the paper centres in the space the
            list leaves rather than on the window, which reads as pushed to one
            side. It tracks the list's width, so collapsing recentres. */}
        <Box
          width={`${gutter}px`}
          flexShrink={0}
          display={{ base: "none", lg: "block" }}
          aria-hidden
          transition="width 0.2s ease"
        />
      </Flex>

      {/* Low and to the right, within reach of the hand already on the paper.
          Fixed, so it stays put while the question scrolls underneath it. */}
      {toolbar && (
        <Box
          position="fixed"
          right={{ base: "12px", md: "24px" }}
          bottom={{ base: "16px", md: "28px" }}
          zIndex={20}
          display={{ base: "none", lg: "block" }}
        >
          {toolbar}
        </Box>
      )}
    </Flex>
  );
};

export default memo(PaperCanvas);
